// Role presentation documents (Business Plan, Terms & Conditions, Final
// Agreement) shown by the role consultation pages: /user_Shoosing/:token
// (userAddingRoleByToken.jsx) and the role proposal modal
// (actPageAssUserExit.jsx, opened from the Footer and admin_associate).
//
// Mirrors server_elakay/elakay_services/roleDocumentAccess.js. The backend is
// the only authority on what may be read; everything here is display logic.
//
// Three distinct role notions -- never mix them:
//   currentRole  the account's real active role (never changed by reading)
//   invitedRole  the role the invitation offers (decided server-side)
//   viewedRole   the role whose documents are on screen (presentation only)

export const ROLE_DOCUMENT_ROLES = Object.freeze([
  Object.freeze({ id: "Royalty", level: 1, keys: Object.freeze({ plan: "planUserRoyal", term: "termConditionRoyal", agree: "royalAgree" }) }),
  Object.freeze({ id: "Seller", level: 2, keys: Object.freeze({ plan: "planUserSeller", term: "termConditionSeller", agree: "sellerAgree" }) }),
  Object.freeze({ id: "Provider", level: 3, keys: Object.freeze({ plan: "planUserProvider", term: "termConditionProvider", agree: "providerAgree" }) }),
  Object.freeze({ id: "Associate", level: 4, keys: Object.freeze({ plan: "planAffaireElakay", term: "termConditionAssociate", agree: "associateAgree" }) }),
  Object.freeze({ id: "Investment", level: 5, keys: Object.freeze({ plan: "planProInvestElakay", term: "termConditionInvest", agree: "investmentAgree" }) }),
  Object.freeze({ id: "Administrator", level: 6, keys: Object.freeze({ plan: "planProAdminElakay", term: "termConditionAdmin", agree: "AdministratorAgree" }) }),
  // The real FrTxtLanguage field names (double "l"), as used by the Language
  // Manager and PageBuilder -- "planAffaireFamily"/"termConditionFamily"/
  // "familyAgree" never existed anywhere in the backend.
  Object.freeze({ id: "Family", level: 7, keys: Object.freeze({ plan: "planProFamillyElakay", term: "termConditionFamilly", agree: "FamillyAgree" }) }),
]);

export const ROLE_IDS = ROLE_DOCUMENT_ROLES.map((role) => role.id);

const ROLE_ALIASES = { Familly: "Family", Investor: "Investment" };

export const canonicalRoleId = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (ROLE_IDS.includes(trimmed)) return trimmed;
  if (ROLE_ALIASES[trimmed]) return ROLE_ALIASES[trimmed];
  return ROLE_IDS.find((id) => id.toLowerCase() === trimmed.toLowerCase()) || null;
};

export const getRoleDefinition = (roleId) =>
  ROLE_DOCUMENT_ROLES.find((role) => role.id === canonicalRoleId(roleId)) || null;

export const getRoleLevel = (roleId) => getRoleDefinition(roleId)?.level || 0;

export const getRoleDocumentKeys = (roleId) => getRoleDefinition(roleId)?.keys || null;

/**
 * Technical id + translated label, so business logic never depends on the
 * display language.
 * @param {(roleId: string, index: number) => string} labelFor
 */
export const buildRoleOptions = (labelFor) =>
  ROLE_DOCUMENT_ROLES.map((role, index) => ({
    id: role.id,
    level: role.level,
    label: (typeof labelFor === "function" && labelFor(role.id, index)) || role.id,
  }));

/**
 * Roles the dropdown may offer: the backend's readableRoles when known,
 * otherwise (still loading / offline) every role up to `fallbackMaxLevel`.
 * The fallback is display-only -- the backend refuses anything else anyway.
 */
export const getReadableRoleIds = (access, fallbackMaxLevel = 0) => {
  if (Array.isArray(access?.readableRoles)) {
    return ROLE_IDS.filter((id) => access.readableRoles.includes(id));
  }
  const max = Number(fallbackMaxLevel) || 0;
  return ROLE_DOCUMENT_ROLES.filter((role) => role.level <= max).map((role) => role.id);
};

/**
 * The role to display: a requested one (e.g. the "r" URL parameter) only when
 * it's readable, otherwise the invited role, otherwise the highest readable.
 */
export const resolveViewedRole = ({ requestedRole, invitedRole, readableRoleIds = [] }) => {
  const requested = canonicalRoleId(requestedRole);
  if (requested && readableRoleIds.includes(requested)) return requested;
  const invited = canonicalRoleId(invitedRole);
  if (invited && readableRoleIds.includes(invited)) return invited;
  return readableRoleIds[readableRoleIds.length - 1] || null;
};

// Replaces the old checkProdKeyExit(): true only when the document holds at
// least one paragraph with something to show. (The old helper destructured
// each key string as [key, val], so it only "worked" by accident, and showed
// "No data" for any array of plain strings.)
export const hasDocumentContent = (value) => {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.some((paragraph) => {
    if (typeof paragraph === "string") return paragraph.trim() !== "";
    if (!paragraph || typeof paragraph !== "object") return false;
    const text = Array.isArray(paragraph.text) ? paragraph.text : [paragraph.text];
    const list = Array.isArray(paragraph.list) ? paragraph.list : [paragraph.list];
    return Boolean(
      (typeof paragraph.title === "string" && paragraph.title.trim()) ||
      text.some((item) => typeof item === "string" && item.trim()) ||
      list.some((item) => typeof item === "string" && item.trim()) ||
      paragraph.imagePath ||
      paragraph.videoPath
    );
  });
};

export const DOCUMENT_STATUS = Object.freeze({
  IDLE: "IDLE",
  LOADING: "LOADING",
  SUCCESS: "SUCCESS",
  EMPTY: "EMPTY",
  FORBIDDEN: "FORBIDDEN",
  EXPIRED: "EXPIRED",
  NETWORK_ERROR: "NETWORK_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
});

// Maps a getRoleDocuments() failure to a display status.
export const statusFromError = (error) => {
  if (!error) return DOCUMENT_STATUS.SERVER_ERROR;
  if (error.network) return DOCUMENT_STATUS.NETWORK_ERROR;
  if (error.status === 410 || error.code === "INVITATION_EXPIRED") return DOCUMENT_STATUS.EXPIRED;
  if (error.status === 401 || error.status === 403) return DOCUMENT_STATUS.FORBIDDEN;
  return DOCUMENT_STATUS.SERVER_ERROR;
};

/**
 * Safe date parsing for countdowns: accepts a Date, an ISO string or a
 * millisecond number; returns a timestamp or null (never NaN/Invalid Date).
 */
export const toTimestamp = (value) => {
  if (value === undefined || value === null || value === "" || value === false) return null;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && /^\d+$/.test(value.trim())) return Number(value.trim());
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

/** Text from the "roleDocuments" section of frontElakayHardcodedText. */
export const roleDocumentsText = (infoSource, key, fallback, values = null) => {
  let text;
  try {
    text = infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.roleDocuments?.[key];
  } catch (error) {
    text = undefined;
  }
  const resolved = typeof text === "string" && text ? text : fallback;
  if (!values) return resolved;
  return resolved.replace(/\{([A-Za-z0-9_]+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name] ?? "") : match
  );
};
